/* PROTON · Review stars on paddle PDPs — pp-stars
 *
 * Renders an HONEST, ATTRIBUTED star-rating row directly under the product title
 * on paddle PDPs. The data is real third-party review data collected by
 * Pickleball Central (an authorized Proton dealer) via their Yotpo widget,
 * harvested by scripts/reviews/harvest-pbc-reviews.py on 2026-09-10.
 *
 * HONESTY RULES (do not weaken):
 *  - Always attributed ("via Pickleball Central") — never presented as our own on-site reviews.
 *  - NO structured data / JSON-LD injection: third-party review counts in our Product
 *    schema would violate Google's self-serving review policy. Visible UI only.
 *  - Products with < MIN_PRODUCT_REVIEWS dealer reviews show the BRAND aggregate line
 *    (clearly labelled "across Proton paddles") instead of a thin product-level claim.
 *  - Numbers are a dated snapshot; refresh via the harvester, then redeploy.
 *
 * Style: flat house strip — no glass, no gradient, no glow. Amber stars, navy score,
 * slate attribution. One line, wraps naturally on mobile.
 */
(function () {
  "use strict";
  if (window.__ppStarsMounted) return;

  var m = location.pathname.match(/\/products\/([a-z0-9-]+)/);
  if (!m) return;
  var handle = m[1];
  // 2026-09-10: the Coyote product handle was renamed project-coyote-preorder →
  // project-coyote (old URL 301s). Everything downstream — DATA/PRESS keys, the
  // rate widget, and the PUBLISHED REVIEWS store (published-reviews.json keys) —
  // is keyed on the ORIGINAL handle, so normalize here and nowhere else.
  if (handle === "project-coyote") handle = "project-coyote-preorder";
  // 2026-09-25 Diego: the Coyote Kit (paddle + backpack) shows the Coyote paddle's proof
  // (podium line, stars, press) — the kit IS that paddle plus the bag.
  if (handle === "coyote-kit") handle = "project-coyote-preorder";

  // PRODUCT-SPECIFIC ONLY (Diego 09-10): each paddle shows its OWN dealer reviews or
  // nothing at all — no brand aggregate, no borrowing across models. The Coyote has no
  // retailer listing yet, so it gets NO stars (rate widget only). Green-trim Peacocks
  // are the same paddle model in a different trim, so they carry their shape's data.
  // Where PBC lists the same model twice (color variants), counts are combined with a
  // weighted average. avg = raw average (fill precision), n = review count.
  var DATA = {
    "series-three-project-flamingo":                { avg: 4.741, n: 116 },
    "series-three-project-flamingo-elongated-13mm": { avg: 4.889, n: 9 },
    "series-three-project-flamingo-square-15mm-new":{ avg: 4.25,  n: 4 },
    "series-three-project-flamingo-square-13mm":    { avg: 5.0,   n: 2 },
    "project-peacock-elongated-15mm":               { avg: 4.8,   n: 10 },
    "project-peacock-elongated-13mm":               { avg: 4.0,   n: 3 },
    "project-peacock-wide-body-15mm":               { avg: 4.5,   n: 8 },
    "project-peacock-wide-body-13mm":               { avg: 5.0,   n: 4 },
    "project-peacock-widebody-13mm-green-trim":     { avg: 5.0,   n: 4 },
    "project-peacock-wide-body-15mm-green-trim":    { avg: 4.5,   n: 8 },
    "series-four-project-roadrunner":               { avg: 4.603, n: 68 }
  };

  // Handles that offer the first-party "Rate this paddle" capture. Submissions go to
  // the CS-desk intake (topic "review") for moderation — they are NOT auto-displayed.
  // Share link that auto-opens the form: /products/<handle>?review=1
  // 2026-10-22: opened to every live paddle so the post-purchase review email (Klaviyo
  // flow V7qTGG, day 14) can deep-link any paddle buyer to /products/<handle>?review=1.
  var RATE_HANDLES = {
    "project-coyote-preorder": "Project Coyote",
    "project-peacock-elongated-15mm": "Project Peacock",
    "project-peacock-elongated-13mm": "Project Peacock",
    "project-peacock-wide-body-15mm": "Project Peacock",
    "project-peacock-wide-body-13mm": "Project Peacock",
    "series-four-project-roadrunner": "Project Roadrunner"
  };
  var INTAKE_URL = "https://protons-mac-mini-1.tailccb751.ts.net/cs/api/intake";

  // Press quotes + tour results shown in the stars area while on-site review counts
  // are thin. Sources are real and attributed: The Kitchen's published Coyote review
  // (quoted in the 2026-08-31 "REVIEWS ARE IN" campaign) and the PPA podium results
  // from the 2026-09-08 "Built for Winning" campaign. Results are stated as history
  // ("first two weeks") so the block never goes stale. Quotes stay VERBATIM excerpts —
  // never paraphrase a reviewer.
  var PRESS = {
    "project-coyote-preorder": {
      quotes: [
        { t: "Proton’s best paddle to date", s: "The Kitchen" },
        { t: "Powerful but also very predictable", s: "The Kitchen", more: true },
        { t: "First Look. REALLY GOOD.", s: "@mattspickleball", more: true }
      ],
      /*__PP_PODIUMS_START__*/
      // AUTO-GENERATED by ~/proton-podiums/build.py from the PPA Tour bracket API
      // (launchd com.proton.podiums). Hand rows live in ~/proton-podiums/manual.json.
      // Never hand-edit between these markers — the next run overwrites it.
      medalLine: "9 golds, 7 silvers + 2 bronzes on the PPA circuit",
      medalShort: "9 golds + 9 more podiums on the PPA circuit",
      podNote: "Played with Project Coyote \u00b7 PPA Tour, Challenger and junior events, Aug\u2013Oct 2026.",
      latest: {"long": "Just in: 🥇 singles + doubles gold, Ottawa", "short": "Just in: 🥇 Ottawa singles + doubles gold", "until": "2026-11-12"},
      podiums: [
        ["SF", "Catherine Parenteau", "Semis · Dbls · Hong Kong"],
        ["🥇", "Catherine Parenteau", "1st · Dbls · Va. Beach"],
        ["SF", "Meghan Dizon", "Semis · Dbls · Va. Beach"],
        ["🥈", "Catherine Parenteau", "2nd · Mixed · Va. Beach"],
        ["SF", "Meghan Dizon", "Semis · Mixed · Va. Beach"],
        ["🥈", "Catherine Parenteau", "2nd · Dbls · Chicago"],
        ["SF", "Meghan Dizon", "Semis · Doubles · Vegas"],
        ["🥈", "Catherine Parenteau", "2nd · Dbls · Vegas"],
        ["SF", "Catherine Parenteau", "Semis · Dbls · Mesa"],
        ["🥈", "Meghan Dizon", "2nd · Doubles · Mesa"],
        ["SF", "Jalina Ingram", "Semis · Singles · Mesa"],
        ["🥇", "Naomi Nguyễn", "1st · Doubles · Atlanta"],
        ["🥈", "Kelly Goodnow", "2nd · Mixed · Atlanta"],
        ["SF", "Naomi Nguyễn", "Semis · Mixed · Atlanta"],
        ["🥈", "Kelly Goodnow", "2nd · Doubles · Seattle"],
        ["🥈", "Jalina Ingram", "2nd · Singles · Seattle"],
        ["🥇", "Kelly Goodnow", "1st · Singles · Ottawa"],
        ["🥇", "Kelly Goodnow", "1st · Doubles · Ottawa"],
        ["🥇🥇", "Gal Solomon", "1st · U12 Singles + Doubles"],
        ["🥇🥇", "Ryland Mehta", "1st · U16 Doubles + Mixed"],
        ["🥇", "Jalina Ingram", "1st · Doubles · Vancouver"],
        ["🥉", "Gal Solomon", "3rd · U12 Mixed Doubles"],
        ["🥉", "Jett Barber", "3rd · U14 Boys’ Doubles"]
      ]
      /*__PP_PODIUMS_END__*/
    }
  };

  var d = DATA[handle];
  if (!d && !RATE_HANDLES[handle]) return; // not a mapped paddle — bats and gear untouched
  var avg = d ? d.avg : 0;
  var n = d ? d.n : 0;
  var scope = "verified reviews";

  function css() {
    if (document.getElementById("pp-stars-css")) return;
    var s = document.createElement("style");
    s.id = "pp-stars-css";
    s.textContent =
      ".pp-stars{display:flex;align-items:center;flex-wrap:wrap;gap:3px 8px;margin:6px 0 2px;line-height:1.2;}" +
      ".pp-stars-row{position:relative;display:inline-block;font-size:16px;letter-spacing:2px;color:#d8dde6;white-space:nowrap;}" +
      ".pp-stars-fill{position:absolute;top:0;left:0;overflow:hidden;color:#e8a012;white-space:nowrap;pointer-events:none;}" +
      ".pp-stars-count{font-size:13px;font-weight:600;color:#0b0b0d;text-decoration:underline;text-underline-offset:3px;background:none;border:none;padding:0;font-family:inherit;line-height:inherit;}" +
      "button.pp-stars-count{cursor:pointer;outline:none;box-shadow:none;}" +
      "button.pp-stars-count:hover{color:#000;}" +
      ".pp-stars-src{font-size:11.5px;color:#5a6b87;flex-basis:100%;}" +
      ".pp-rate-open{background:none;border:none;padding:0;margin:2px 0 0;font-size:12px;font-weight:500;color:#0b0b0d;text-decoration:underline;text-underline-offset:3px;cursor:pointer;outline:none;box-shadow:none;}" +
      ".pp-rate-open:hover{color:#0b0b0d;}" +
      ".pp-rate-open:focus,.pp-rate-open:focus-visible{outline:none;box-shadow:none;}" +
      ".pp-rate{display:none;margin:10px 0 4px;padding:14px 16px;border:1px solid #e3e6ec;border-radius:8px;max-width:400px;background:#fff;}" +
      ".pp-rate.is-open{display:block;}" +
      ".pp-rate-h{font-size:13px;font-weight:700;color:#0b0b0d;margin:0 0 6px;line-height:1.35;}" +
      ".pp-rate-pick{font-size:22px;letter-spacing:3px;color:#d8dde6;cursor:pointer;user-select:none;line-height:1;margin:0 0 10px;}" +
      ".pp-rate-pick span{cursor:pointer;}" +
      ".pp-rate-pick span.on{color:#e8a012;}" +
      ".pp-rate input,.pp-rate textarea{display:block;width:100%;box-sizing:border-box;margin:0 0 6px;padding:7px 9px;border:1px solid #d8dde6;border-radius:5px;font-family:inherit;font-size:12.5px;line-height:1.4;color:#3f5069;background:#fff;min-height:0;}" +
      ".pp-rate input:focus,.pp-rate textarea:focus{outline:none;border-color:#0b0b0d;box-shadow:none;}" +
      ".pp-rate textarea{min-height:52px;resize:vertical;}" +
      ".pp-rate-cols{display:flex;gap:6px;}" +
      ".pp-rate-cols input{flex:1;}" +
      ".pp-rate-btn{display:inline-block;background:#0b0b0d;color:#fff;border:none;border-radius:5px;padding:8px 16px;margin-top:2px;font-family:inherit;font-size:12.5px;font-weight:700;letter-spacing:.2px;cursor:pointer;}" +
      ".pp-rate-btn:hover{background:#000;}" +
      ".pp-rate-btn[disabled]{opacity:.5;cursor:default;}" +
      ".pp-rate-msg{font-size:11.5px;margin-top:6px;color:#5a6b87;}" +
      ".pp-rate-msg:empty{display:block;margin:0;}" +
      ".pp-rate-msg.err{color:#b3401f;}" +
      ".pp-code{border:1px dashed #0b0b0d;border-radius:8px;padding:12px;text-align:center;margin-top:10px;}" +
      ".pp-code-eyebrow{font-size:10.5px;font-weight:700;letter-spacing:1.5px;text-transform:uppercase;color:#5a6b87;}" +
      ".pp-code-code{font-size:22px;font-weight:800;letter-spacing:2px;color:#0b0b0d;margin-top:2px;}" +
      ".pp-code-sub{font-size:11.5px;line-height:1.6;color:#5a6b87;margin-top:4px;}" +
      ".pp-code-sub a{color:#0b0b0d;font-weight:600;text-decoration:underline;text-underline-offset:2px;}" +
      ".pp-amp{margin-top:10px;padding:12px;border:1px solid #e3e6ec;border-radius:8px;}" +
      ".pp-amp-h{font-size:12.5px;font-weight:700;color:#0b0b0d;}" +
      ".pp-amp-t{font-size:11.5px;line-height:1.55;color:#5a6b87;margin-top:4px;}" +
      ".pp-amp-q{font-size:11.5px;font-style:italic;line-height:1.5;color:#3f5069;border-left:2px solid #d8dde6;padding:4px 8px;margin:8px 0;}" +
      ".pp-amp-copy{background:#fff;border:1px solid #0b0b0d;border-radius:5px;color:#0b0b0d;font-family:inherit;font-size:11.5px;font-weight:700;padding:6px 12px;margin-right:10px;cursor:pointer;}" +
      ".pp-amp-fb{display:inline-block;font-size:11.5px;font-weight:700;color:#0b0b0d;text-decoration:underline;text-underline-offset:2px;margin-top:6px;}" +
      ".pp-amp-go{display:inline-block;background:#0b0b0d;color:#fff !important;border-radius:5px;padding:9px 16px;margin-top:8px;font-size:12.5px;font-weight:700;letter-spacing:.2px;text-decoration:none;cursor:pointer;}" +
      ".pp-amp-go:hover{background:#000;}" +
      ".pp-amp-hint{font-size:11.5px;line-height:1.55;color:#5a6b87;margin-top:8px;}" +
      ".pp-amp-hint:empty{display:none;}" +
      "@media (max-width:479px){.pp-rate-cols{display:block;}}" +
      ".pp-revs{display:none;margin:10px 0 4px;padding:4px 16px;border:1px solid #e3e6ec;border-radius:8px;max-width:400px;max-height:340px;overflow-y:auto;background:#fff;}" +
      ".pp-revs-cta{padding:10px 0;border-bottom:1px solid #eef0f4;}" +
      ".pp-revs-cta:empty{display:none;}" +
      ".pp-revs-cta .pp-rate-open{margin:0;}" +
      ".pp-revs .pp-rate{border:none;border-radius:0;padding:10px 0 4px;margin:0;max-width:none;}" +
      ".pp-revs.is-open{display:block;}" +
      ".pp-rev{padding:10px 0;border-bottom:1px solid #eef0f4;}" +
      ".pp-rev:last-child{border-bottom:none;}" +
      ".pp-rev-top{display:flex;align-items:center;flex-wrap:wrap;gap:3px 8px;}" +
      ".pp-rev-stars{position:relative;display:inline-block;font-size:13px;letter-spacing:1.5px;color:#d8dde6;white-space:nowrap;line-height:1.2;}" +
      ".pp-rev-name{font-size:12.5px;font-weight:700;color:#0b0b0d;}" +
      ".pp-rev-v{font-size:10.5px;font-weight:600;color:#2c7a1e;border:1px solid #bfe0b4;border-radius:999px;padding:1px 7px;}" +
      ".pp-rev-d{font-size:11px;color:#8a94a8;margin-left:auto;}" +
      ".pp-rev-t{font-size:12.5px;line-height:1.5;color:#3f5069;margin-top:4px;}" +
      ".pp-aux{display:flex;flex-wrap:wrap;align-items:baseline;gap:2px 16px;}" +
      ".pp-aux>div{min-width:0;}" +
      ".pp-press{margin:0;max-width:460px;}" +
      ".pp-press-slot{min-height:18px;}" +
      ".pp-press-slide{font-size:12px;line-height:1.4;color:#3f5069;margin:0;opacity:1;transform:none;transition:opacity .28s ease,transform .28s ease;}" +
      ".pp-press-slide.out{opacity:0;transform:translateY(6px);}" +
      ".pp-press-slide .st{color:#e8a012;margin-right:2px;}" +
      ".pp-press-slide .src{color:#8a94a8;font-weight:600;white-space:nowrap;}" +
      ".pp-press-slide .med{font-weight:700;color:#0b0b0d;}" +
      ".pp-med-long{display:inline;}.pp-med-short{display:none;}" +
      ".pp-medals{font-size:13px;font-weight:700;color:#0b0b0d;margin:0;line-height:1.4;}" +
      ".pp-latest{display:inline-block;margin:6px 0 6px;padding:5px 12px;border-radius:999px;font-size:12.5px;font-weight:700;line-height:1.35;color:#7a2e00;background:linear-gradient(135deg,rgba(255,96,0,.16),rgba(255,178,122,.22));border:1px solid rgba(255,96,0,.38);box-shadow:0 0 18px rgba(255,96,0,.22);}" +
      ".pp-latest .pp-l-short{display:none;}" +
      ".pp-medals .pp-rate-open{margin:0 0 0 8px;font-weight:500;}" +
      ".pp-podiums{display:none;margin:8px 0 2px;padding:4px 16px;border:1px solid #e3e6ec;border-radius:8px;max-width:400px;background:#fff;}" +
      ".pp-podiums.is-open{display:block;}" +
      ".pp-pod{display:flex;align-items:baseline;gap:8px;padding:7px 0;border-bottom:1px solid #eef0f4;font-size:12.5px;line-height:1.4;}" +
      ".pp-pod:last-child{border-bottom:none;}" +
      ".pp-pod-n{font-weight:700;color:#0b0b0d;white-space:nowrap;}" +
      ".pp-pod-e{color:#5a6b87;}" +
      ".pp-pod-h{font-size:10.5px;font-weight:800;letter-spacing:.6px;text-transform:uppercase;color:#8a94a8;padding:10px 0 2px;}" +
      ".pp-pod+.pp-pod-h{border-top:1px solid #eef0f4;margin-top:2px;}" +
      ".pp-pod-b{font-size:9.5px;font-weight:800;letter-spacing:.6px;color:#5a6b87;border:1px solid #d8dde6;border-radius:4px;padding:1px 4px;white-space:nowrap;}" +
      ".pp-pod-note{font-size:11px;color:#8a94a8;padding:7px 0 8px;}" +
      ".pp-acc{margin-top:8px;}" +
      ".pp-acc-h{display:flex;justify-content:space-between;align-items:center;width:100%;background:none;border:none;border-top:1px solid #e3e6ec;padding:14px 2px;margin:0;font-family:inherit;font-size:14px;font-weight:700;color:#0b0b0d;letter-spacing:.2px;cursor:pointer;text-align:left;outline:none;box-shadow:none;}" +
      ".pp-acc-h:hover{color:#000;}" +
      ".pp-acc-h:last-of-type:not(.open)~.pp-acc-b:last-child{border-bottom:1px solid #e3e6ec;}" +
      ".pp-acc>.pp-acc-b:last-of-type{border-bottom:1px solid #e3e6ec;}" +
      ".pp-acc-c{width:9px;height:9px;border-right:2px solid #0b0b0d;border-bottom:2px solid #0b0b0d;transform:rotate(45deg);transition:transform .22s ease;margin:-4px 8px 0 12px;flex:none;}" +
      ".pp-acc-h.open .pp-acc-c{transform:rotate(225deg);margin-top:4px;}" +
      ".pp-acc-b{display:none;padding:0 2px 16px;}" +
      ".pp-acc-b.open{display:block;}" +
      ".pp-acc-b>*:first-child{margin-top:0;}" +
      "@media (max-width:749px){.pp-stars{margin:4px 0 0;}.pp-stars-row{font-size:15px;}.pp-stars-count{font-size:12.5px;}.pp-rate{max-width:none;}" +
      ".pp-press-slide{font-size:12px;}.pp-press-slot{min-height:17px;}.pp-medals{font-size:12px;}" +
      ".pp-med-long{display:none;}.pp-med-short{display:inline;}.pp-latest .pp-l-long{display:none;}.pp-latest .pp-l-short{display:inline;}}";
    document.head.appendChild(s);
  }

  function rateWidget(el) {
    var productName = RATE_HANDLES[handle];
    if (!productName) return;
    var ratedKey = "pp-rated-" + handle;
    var already = null;
    try { already = localStorage.getItem(ratedKey); } catch (e) {}
    var wrap = document.createElement("div");
    if (already) {
      wrap.innerHTML = '<span class="pp-stars-src">You rated this paddle ' + already + '★ — leave the same review on <a href="https://maps.google.com/?cid=9369318437762954233" target="_blank" rel="noopener" style="color:#0b0b0d;font-weight:600;">Google</a>. It helps players who have never heard of us.</span>';
      el.appendChild(wrap);
      return;
    }
    // Lives INSIDE the reviews panel (Diego 09-10: the buy box carries no
    // standalone rate/read links) — short opener, incentive kept.
    wrap.innerHTML =
      '<button type="button" class="pp-rate-open">Leave a review</button>' +
      '<div class="pp-rate" role="form" aria-label="Rate this paddle">' +
      '<p class="pp-rate-h">Rate your ' + productName + "</p>" +
      '<div class="pp-rate-pick" aria-label="Choose 1 to 5 stars">' +
      "<span data-v='1'>★</span><span data-v='2'>★</span><span data-v='3'>★</span><span data-v='4'>★</span><span data-v='5'>★</span></div>" +
      '<textarea maxlength="1200" placeholder="What did you think? (optional)"></textarea>' +
      '<div class="pp-rate-cols">' +
      '<input type="text" maxlength="120" placeholder="Name">' +
      '<input type="email" maxlength="160" placeholder="Email">' +
      "</div>" +
      '<button type="button" class="pp-rate-btn">Submit review</button>' +
      '<div class="pp-rate-msg"></div></div>';
    el.appendChild(wrap);

    var open = wrap.querySelector(".pp-rate-open");
    var form = wrap.querySelector(".pp-rate");
    var pick = wrap.querySelector(".pp-rate-pick");
    var starEls = pick.querySelectorAll("span");
    var msg = wrap.querySelector(".pp-rate-msg");
    var btn = wrap.querySelector(".pp-rate-btn");
    var rating = 0;

    open.addEventListener("click", function () {
      form.classList.toggle("is-open");
    });
    // Shareable review link: /products/<handle>?review=1 (or #rate) auto-opens
    // the reviews panel AND the form inside it.
    if (/[?&]review=1/.test(location.search) || location.hash === "#rate") {
      var host = document.getElementById("pp-revs");
      if (host) host.classList.add("is-open");
      form.classList.add("is-open");
      setTimeout(function () {
        try { form.scrollIntoView({ behavior: "smooth", block: "center" }); } catch (e) { form.scrollIntoView(); }
      }, 600);
    }
    pick.addEventListener("click", function (ev) {
      var v = ev.target && ev.target.getAttribute("data-v");
      if (!v) return;
      rating = Number(v);
      for (var i = 0; i < starEls.length; i++) starEls[i].classList.toggle("on", i < rating);
    });
    btn.addEventListener("click", function () {
      var comment = wrap.querySelector("textarea").value.trim();
      var nameV = wrap.querySelector('input[type="text"]').value.trim();
      var emailV = wrap.querySelector('input[type="email"]').value.trim();
      msg.className = "pp-rate-msg";
      if (!rating) { msg.className += " err"; msg.textContent = "Tap a star rating first."; return; }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailV)) { msg.className += " err"; msg.textContent = "A valid email is required (we use it to verify your purchase)."; return; }
      btn.disabled = true;
      msg.textContent = "Sending…";
      fetch(INTAKE_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          topic: "review",
          email: emailV,
          name: nameV || "Anonymous",
          fields: {
            product: productName,
            product_handle: handle,
            rating: rating + " / 5",
            review_text: comment || "(no comment)",
            source: "PDP rate widget",
          },
        }),
      })
        .then(function (r) { return r.json().catch(function () { return {}; }); })
        .then(function (j) {
          if (j && j.ok) {
            try { localStorage.setItem(ratedKey, String(rating)); } catch (e) {}
            // ⭐ UN-GATED 2026-09-21 (Diego: 2-step flow — "after 14 days ask for a review
            // for a 10% discount, and once they place it they receive the following email
            // with thank you and here is your 10% code"). The reward is now earned by the
            // REVIEW ITSELF, so the old Google gate ("leave a review on Google to unlock
            // your 10%") is both wrong and a Google Maps policy violation (incentivised
            // Google reviews = Fake Engagement, penalty lands on OUR listing). The Google
            // ask STAYS as a no-strings multiplier — it just no longer buys the code.
            // The code is still never PRINTED in this UI; it arrives by email.
            form.innerHTML = '<p class="pp-rate-h">Thanks — your ' + rating + "★ review is in.</p>" +
              '<div class="pp-amp"><div class="pp-amp-h">Thank you. It genuinely helps other players.</div>' +
              '<div class="pp-amp-t">One more favor? The same words on our Google listing reach players who have never heard of us.</div>' +
              (comment ? '<div class="pp-amp-q">&ldquo;' + esc(comment) + '&rdquo;</div>' : "") +
              '<a class="pp-amp-go" href="https://maps.google.com/?cid=9369318437762954233" target="_blank" rel="noopener">' + (comment ? "Copy &amp; leave it on Google &rarr;" : "Leave it on Google &rarr;") + "</a>" +
              '<div class="pp-amp-hint">' + (comment ? "One tap copies your review and opens our Google listing &mdash; just hit Paste in the review box." : "") + "</div></div>";
            // Copy must survive in-app browsers (Gmail/Instagram WebViews), where the
            // async clipboard API silently REJECTS — the original handler had no
            // rejection path, so the tap did nothing (caught live by Diego's friend
            // 09-10). Order: async API -> execCommand textarea fallback -> tell the
            // user to long-press. Google reviews can NEVER be pre-filled by URL, so
            // clipboard + paste is the ceiling — say so in the feedback text.
            // ONE action (Diego 09-10: "when they click the link it auto copies…all
            // they have to do is hit paste"): the Google button copies their review
            // AND opens the listing in the same tap. Copy must survive in-app
            // browsers (Gmail/IG WebViews) where the async clipboard API silently
            // REJECTS: async API → execCommand-textarea fallback → long-press hint.
            // Google review boxes can NEVER be pre-filled by URL — copy+paste is
            // the ceiling, so the hint says exactly what to do.
            var hint = form.querySelector(".pp-amp-hint");
            function copyReview() {
              function ok() { if (hint) hint.innerHTML = "Copied ✓ &mdash; now just hit <b>Paste</b> in the Google review box."; }
              function legacy() {
                try {
                  var ta = document.createElement("textarea");
                  ta.value = comment;
                  ta.setAttribute("readonly", "");
                  ta.style.cssText = "position:fixed;top:-999px;left:0;opacity:0;";
                  document.body.appendChild(ta);
                  ta.select();
                  ta.setSelectionRange(0, comment.length);
                  var done = document.execCommand("copy");
                  document.body.removeChild(ta);
                  if (done) ok();
                  else if (hint) hint.textContent = "Couldn't auto-copy — press & hold your review above to copy it.";
                } catch (e) { if (hint) hint.textContent = "Couldn't auto-copy — press & hold your review above to copy it."; }
              }
              try {
                if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(comment).then(ok, legacy);
                else legacy();
              } catch (e) { legacy(); }
            }
            var gl = form.querySelector(".pp-amp-go");
            if (gl && comment) gl.addEventListener("click", function () { copyReview(); });
            open.style.display = "none";
            form.classList.add("is-open");
          } else {
            btn.disabled = false;
            msg.className += " err";
            msg.textContent = (j && j.error) === "too many requests" ? "Too many submissions from this connection — try again later." : "Couldn’t send — please try again.";
          }
        })
        .catch(function () {
          btn.disabled = false;
          msg.className += " err";
          msg.textContent = "Couldn’t send — please try again.";
        });
    });
  }

  function esc(s) {
    return String(s == null ? "" : s).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }

  // First-party reviews published by scripts/reviews/publish-review.js.
  // PRIMARY: our own server (always fresh — a new review is live instantly).
  // FALLBACK: baked in below at publish time (uploadAsset() in publish-review.js
  // string-replaces the sentinel object between the markers with the current
  // published-reviews.json payload before uploading THIS file).
  //
  // 🚨 2026-09-10 fix: the fallback used to be a SEPARATE fetch to
  // assets/proton-reviews-data.json. Shopify's CDN caches that plain asset for
  // up to a year and IGNORES query strings, so a re-publish never reached real
  // visitors who lost the primary-API race — the fallback was found stuck at
  // the very first review for hours. This file (proton-review-stars.js) gets a
  // fresh Shopify asset_url fingerprint on every edit, which DOES bust the
  // cache, so baking the data in here instead of fetching it separately makes
  // the fallback un-stale by construction — no second network call needed.
  var FIRST_PARTY_FALLBACK = /*__PP_FALLBACK_START__*/ {"v":1,"reviews":{"project-coyote-preorder":[{"r":5,"n":"Diego","t":"Love the look! Fantastic pop and the taper is a game changer.","d":"2026-09-10","v":true},{"r":5,"n":"Ryder M.","t":"Best Paddle so far!!!!","d":"2026-09-10","v":true},{"r":5,"n":"Kiah O.","t":"Loving my new paddle! It’s the best paddle I have used.","d":"2026-09-10","v":true},{"r":5,"n":"Karson B.","t":"Really great feel of the face for dinking and amazing driving experience","d":"2026-09-10","v":true},{"r":5,"n":"Cole","t":"When i first got it i couldnt believe how light AND powerful it was. such a rare combo. definitely my favorite proton paddle and everyday carry","d":"2026-09-10","v":true},{"r":5,"n":"David","t":"My absolute favorite paddle I’ve ever owned!","d":"2026-09-10","v":true},{"r":5,"n":"Liv","t":"Love the the colors and the design\nTop 1 paddle in the market!","d":"2026-09-10","v":true},{"r":5,"n":"Reggie L.","t":"The taper and power is my favorite. been winning so many more matches.. feel like my power is way better. and surprisingly great control also. Best thing since the flamingo. only better","d":"2026-09-10","v":true},{"r":5,"n":"Robert K.","t":"Outstanding.","d":"2026-09-10","v":true},{"r":5,"n":"Jill","t":"I’m an intermediate player and ordered it because of the reviews saying it has pop.  I was a little nervous though as I had also read it’s not as stable as other paddles.  That being said I am absolutely loving this paddle.  It’s much lighter than my previous paddle and not only is it giving me the extra pop to get my resets and dinks over the net, it’s also giving me much better control with battles at the net.  I’m so glad I found the Coyote!!","d":"2026-09-10","v":true},{"r":5,"n":"Raymond H.","t":"Great looking paddle. Light and fast. Nice power. Unfortunately no cover available.","d":"2026-09-10","v":true},{"r":5,"n":"Erik","t":"Getting used to the new paddle still","d":"2026-09-10","v":true},{"r":5,"n":"Emma Y.","t":"I was honestly nervous to spend this much on a paddle, but I’m so glad I did. The Coyote feels so good and I noticed a difference right away. I really don’t see myself using another paddle going forward.","d":"2026-09-10","v":false},{"r":5,"n":"Brent P.","t":"Great paddle, I think it’s making my wife love me again because she gets ecstatic when we win together","d":"2026-09-10","v":false},{"r":5,"n":"Ryan","t":"I absolutely love it !","d":"2026-09-10","v":true},{"r":5,"n":"Peter W.","t":"I’m a huge fan of the Flamingo. I didn’t think that Proton could improve upon it, but with the Coyote I’ll eat my words. Sweet spot has been improved by 20-25%. Also I feel that I can more easily shape the arc of the ball. Coyote is a fun paddle.","d":"2026-09-11","v":true},{"r":5,"n":"Michael","t":"Amazing Paddle. This is my 4th Proton. Every time I think it cannot get better, it has!","d":"2026-09-11","v":true},{"r":5,"n":"Gary J.","t":"I LOVE the new Coyote paddle!!  Great look, feel, control, and power! All the Proton paddles are great, but the Coyote may be my favorite. I’ll get another one for sure to have and let others try!","d":"2026-09-12","v":true},{"r":5,"n":"Sandy M.","t":"I love the power the pop and control","d":"2026-09-13","v":true},{"r":5,"n":"Erik E.","t":"Good power on the sweet spot. 8.0 oz weight is just right for kitchen battles. Excellent control.","d":"2026-09-14","v":true},{"r":5,"n":"onlytintin","t":"Light weight but powerful! Love!","d":"2026-09-14","v":true},{"r":1,"n":"Nghiep L.","t":"It’s The Same As The Other Previous Proton  Paddles & Just Changes The Name & I’m So Disappointed","d":"2026-09-14","v":true},{"r":5,"n":"Abe","t":"Love it, fits my style of play like a glove.","d":"2026-09-22","v":true},{"r":5,"n":"Darlene B.","t":"Big proton fan with the three flamingo series.  Love it because of the balance of soft and power.  Coyote brings that balance to new level.  Love it\n\nMy only complaint is that I buy proton paddles (I have 4) and never seem to get the free hat promotion!","d":"2026-09-22","v":true},{"r":5,"n":"Dina R.","t":"I've used the Coyote since the day it was released. Coming from another famous power paddle, I don't feel like I've lost power, I have it, but I gained a better feel for the ball.  It has only gotten a better feel with time.","d":"2026-09-22","v":true},{"r":5,"n":"Bill","t":"Love the control while having amazing Pop.","d":"2026-09-22","v":true},{"r":5,"n":"Francis M.","t":"Great paddle, light and fast. Money worth","d":"2026-09-22","v":true},{"r":5,"n":"Hootie","t":"Absolutely love mine. Spin off the paddle is amazing and so is the pop on drives.","d":"2026-09-22","v":true},{"r":5,"n":"Edgar","t":"Light and fast","d":"2026-09-22","v":true},{"r":2,"n":"Terry","t":"It’s a lightweight paddle that didn’t fit my game, it had decent pop but my Joola I feel has more power and better control. I returned it","d":"2026-09-22","v":true},{"r":4,"n":"Joe V.","t":"Love the paddle the only issue is that around the edge of the paddle when hitting the ball, the ball comes off dead or different than other gen 3 paddles","d":"2026-09-22","v":true},{"r":5,"n":"Tiana M.","t":"I absolutely love this paddle, best paddle i have bought, the power, the spin, the drive, the control that comes off this paddle i have absolutely no complaints","d":"2026-10-21","v":true}]}} /*__PP_FALLBACK_END__*/;
  var REVIEWS_API = "https://protons-mac-mini-1.tailccb751.ts.net/cs/api/reviews";
  function pick(j) { return (j && j.reviews && j.reviews[handle]) || []; }
  // 🚨 2026-09-21 (found by the full audit): the BAKED fallback is keyed by handle
  // directly — {"project-coyote-preorder":[…]} — while the live API wraps its data in
  // {reviews:{…}}. Feeding the baked object to pick() therefore looked for `.reviews`,
  // found nothing, and returned []. Net effect: every time the reviews API was slow or
  // unreachable, the stars, the review panel and the rate widget silently VANISHED from
  // the PDP — the safety net had never once caught anything. Accept BOTH shapes.
  function pickFallback() {
    var f = FIRST_PARTY_FALLBACK || {};
    return (f.reviews ? f.reviews[handle] : f[handle]) || [];
  }
  function fetchFirstParty() {
    // 4s, not 2.5s: the tailnet funnel's cold-start TLS regularly loses a 2.5s race,
    // dropping visitors onto the embedded fallback below.
    var timeout = new Promise(function (res) { setTimeout(function () { res(null); }, 4000); });
    var live = fetch(REVIEWS_API, { cache: "no-store" })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
    return Promise.race([live, timeout]).then(function (j) {
      if (j) {
        var live = pick(j);
        if (live.length) return live;
      }
      return pickFallback();
    });
  }

  function starsRow(value, cls) {
    var stars = "★★★★★";
    var pct = Math.max(0, Math.min(100, (value / 5) * 100));
    return '<span class="' + cls + '" aria-hidden="true">' + stars +
      '<span class="pp-stars-fill" style="width:' + pct.toFixed(1) + '%">' + stars + "</span></span>";
  }

  function mount(fp) {
    if (document.getElementById("pp-stars-anchor")) return true;
    var title =
      document.querySelector(".product__info-container .product__title") ||
      document.querySelector(".product__info-wrapper .product__title") ||
      document.querySelector(".product__title");
    if (!title) return false;
    css();
    var fpN = fp.length;
    var fpSum = 0;
    for (var i = 0; i < fpN; i++) fpSum += fp[i].r || 0;
    var combN = n + fpN;
    var combAvg = combN ? (avg * n + fpSum) / combN : 0;
    var src = n && fpN ? "via Pickleball Central &amp; Proton customers"
      : n ? "via Pickleball Central" : "from Proton customers";
    // LAYOUT (Diego 09-10, FINAL): gold results line directly UNDER THE TITLE;
    // the whole review section (stars → rotating quote → rate/read row) directly
    // UNDER THE PRICE. The price row is the container child holding `.price`.
    medalBlock(title);
    var priceEl = document.querySelector(".product__info-container .price");
    var priceRow = null;
    try { priceRow = priceEl && priceEl.closest(".product__info-container > *"); } catch (e) {}
    var anchor = priceRow || title;
    if (combN > 0) {
      var el = document.createElement("div");
      el.id = "pp-stars";
      el.className = "pp-stars";
      el.setAttribute("aria-label", "Rated " + combAvg.toFixed(1) + " out of 5 from " + combN + " verified reviews");
      // FEWER WORDS (Diego 09-10): no standalone "Read reviews" or "Own it? Rate
      // this paddle" links. The "N Reviews" count IS the toggle — the panel it
      // opens carries the reviews AND the leave-a-review option.
      var interactive = fpN > 0 || !!RATE_HANDLES[handle];
      var countTxt = combN.toLocaleString("en-US") + (combN === 1 ? " Review" : " Reviews");
      el.innerHTML =
        starsRow(combAvg, "pp-stars-row") +
        (interactive
          ? '<button type="button" class="pp-stars-count" aria-expanded="false">' + countTxt + "</button>"
          : '<span class="pp-stars-count">' + countTxt + "</span>") +
        '<span class="pp-stars-src">' + combAvg.toFixed(1) + " average &middot; " + src + "</span>";
      anchor.insertAdjacentElement("afterend", el);
      anchor = el;
    }
    var marker = document.createElement("span");
    marker.id = "pp-stars-anchor";
    marker.style.display = "none";
    anchor.insertAdjacentElement("afterend", marker);
    reviewsPanel(marker, fp);
    pressBlock(marker, fp);
    var countBtn = el ? el.querySelector("button.pp-stars-count") : null;
    if (countBtn) countBtn.addEventListener("click", function () {
      var panel = document.getElementById("pp-revs");
      if (!panel) return;
      var open = panel.classList.toggle("is-open");
      countBtn.setAttribute("aria-expanded", open ? "true" : "false");
    });
    return true;
  }

  // Static gold results line under the product title, with the in-flow podium
  // list behind a flat toggle. NOT part of the quote rotation and NOT floating
  // (Diego 09-10: under the name, always visible).
  function medalBlock(title) {
    var p = PRESS[handle];
    if (!p) return;
    // Pure award stack: golds first (double golds ahead of singles), then
    // silver, bronze, then non-medal finishes like a semifinal. Sorting here
    // — not in the data — means a row added anywhere still lands in the right
    // place. A marker that is not a medal emoji (e.g. "SF") renders as a text
    // chip and is NOT aria-hidden: "Semifinalist" carries meaning, a glyph does not.
    var rows = (p.podiumGroups || []).reduce(function (acc, g) {
      return acc.concat(g.rows || []);
    }, []).concat(p.podiums || []);
    var ORDER = ["\uD83E\uDD47", "\uD83E\uDD48", "\uD83E\uDD49"]; // gold, silver, bronze
    function tier(m) {
      for (var i = 0; i < ORDER.length; i++) if (m.indexOf(ORDER[i]) >= 0) return i;
      return ORDER.length;
    }
    function count(m) {
      var t = tier(m);
      return t < ORDER.length ? m.split(ORDER[t]).length - 1 : 0;
    }
    rows = rows.map(function (r, i) { return { r: r, i: i }; }).sort(function (a, b) {
      return tier(a.r[0]) - tier(b.r[0]) || count(b.r[0]) - count(a.r[0]) || a.i - b.i;
    }).map(function (x) { return x.r; });

    var pods = "";
    for (var k = 0; k < rows.length; k++) {
      var row = rows[k];
      var mark = /^[A-Z]{1,3}$/.test(row[0])
        ? '<span class="pp-pod-b">' + esc(row[0]) + "</span>"
        : '<span aria-hidden="true">' + row[0] + "</span>";
      pods += '<div class="pp-pod">' + mark +
        '<span class="pp-pod-n">' + esc(row[1]) + "</span>" +
        '<span class="pp-pod-e">' + esc(row[2]) + "</span></div>";
    }
    pods += '<div class="pp-pod-note">' + esc(p.podNote || "Played with Project Coyote \u00b7 PPA Tour, Challenger and junior events.") + '</div>';

    var div = document.createElement("div");
    div.className = "pp-golds";
    var lt = p.latest, latest = "";
    if (lt && lt.long && new Date().toISOString().slice(0, 10) <= String(lt.until || "")) {
      latest = '<p class="pp-latest"><span class="pp-l-long">' + esc(lt.long) + '</span>' +
        '<span class="pp-l-short">' + esc(lt.short || lt.long) + "</span></p>";
    }
    div.innerHTML = latest +
      '<p class="pp-medals">🥇 <span class="pp-med-long">' + esc(p.medalLine) + "</span>" +
      '<span class="pp-med-short">' + esc(p.medalShort || p.medalLine) + "</span>" +
      '<button type="button" class="pp-rate-open pp-pod-toggle">See the podiums</button></p>' +
      '<div class="pp-podiums">' + pods + "</div>";
    title.insertAdjacentElement("afterend", div);
    div.querySelector(".pp-pod-toggle").addEventListener("click", function () {
      div.querySelector(".pp-podiums").classList.toggle("is-open");
    });
  }

  // Press quotes + customer 5★ reviews as ONE rotating line (Diego 09-10: "juggle
  // all the 5-star reviews"). ORDER RULE (Diego): every page load starts with the
  // brand-name quotes (The Kitchen first, PRESS order), then the 5★ customer
  // reviews NEWEST FIRST. Each slide is truncated to ONE line — a wrapping slide
  // grows the slot and the whole buy box jumps every rotation.
  // Rotation pauses on slot hover/touch; prefers-reduced-motion gets a static quote.
  function oneLine(s, max) {
    s = String(s || "").replace(/\s+/g, " ").trim();
    if (s.length <= max) return s;
    var cut = s.slice(0, max);
    var sp = cut.lastIndexOf(" ");
    return (sp > max - 14 ? cut.slice(0, sp) : cut).replace(/[\s,;:.!-]+$/, "") + "…";
  }
  function pressBlock(el, fp) {
    var p = PRESS[handle];
    if (!p) return;
    var slides = [];
    for (var i = 0; i < p.quotes.length; i++) {
      var q = p.quotes[i];
      slides.push('<span class="st" aria-hidden="true">★</span>' +
        "“" + esc(q.t) + "”" +
        (q.s ? ' <span class="src">&mdash; ' + esc(q.s) + "</span>" : ""));
    }
    // 5★ customer reviews with text, newest first (stable sort by date desc).
    var five = [];
    for (var f = 0; f < (fp || []).length; f++) {
      var rv = fp[f];
      if ((rv.r || 0) === 5 && rv.t) five.push(rv);
    }
    five.sort(function (a, b) { return String(b.d || "").localeCompare(String(a.d || "")); });
    for (var k2 = 0; k2 < five.length; k2++) {
      var nm = oneLine(five[k2].n || "A Proton customer", 16);
      slides.push('<span class="st" aria-hidden="true">★</span>' +
        "“" + esc(oneLine(five[k2].t, 34)) + "”" +
        ' <span class="src">&mdash; ' + esc(nm) + "</span>");
    }
    var wrap = document.createElement("div");
    wrap.className = "pp-press";
    wrap.innerHTML = '<div class="pp-press-slot"><div class="pp-press-slide">' + slides[0] + "</div></div>";
    el.insertAdjacentElement("afterend", wrap);

    var slot = wrap.querySelector(".pp-press-slot");
    var paused = false;
    slot.addEventListener("mouseenter", function () { paused = true; });
    slot.addEventListener("mouseleave", function () { paused = false; });
    slot.addEventListener("touchstart", function () {
      paused = true;
      setTimeout(function () { paused = false; }, 8000);
    }, { passive: true });

    var reduce = false;
    try { reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches; } catch (e) {}
    if (reduce || slides.length < 2) return; // static first quote, gold line already always-on
    var idx = 0;
    setInterval(function () {
      if (paused || document.hidden) return;
      var s = slot.querySelector(".pp-press-slide");
      s.classList.add("out");
      setTimeout(function () {
        idx = (idx + 1) % slides.length;
        s.innerHTML = slides[idx];
        s.classList.remove("out");
      }, 300);
    }, 4500);
  }

  // The one reviews panel, toggled by the "N Reviews" count in the stars row.
  // Leave-a-review lives at the TOP of the panel so it is visible on open.
  function reviewsPanel(el, fp) {
    if (!fp.length && !RATE_HANDLES[handle]) return;
    var items = "";
    for (var i = 0; i < fp.length; i++) {
      var r = fp[i];
      items +=
        '<div class="pp-rev">' +
        '<div class="pp-rev-top">' + starsRow(r.r || 0, "pp-rev-stars") +
        '<span class="pp-rev-name">' + esc(r.n || "Anonymous") + "</span>" +
        (r.v ? '<span class="pp-rev-v">Verified buyer</span>' : "") +
        '<span class="pp-rev-d">' + esc(r.d || "") + "</span></div>" +
        (r.t ? '<div class="pp-rev-t">' + esc(r.t) + "</div>" : "") +
        "</div>";
    }
    var panel = document.createElement("div");
    panel.className = "pp-revs";
    panel.id = "pp-revs";
    panel.innerHTML = '<div class="pp-revs-cta"></div>' + items;
    el.insertAdjacentElement("afterend", panel);
    rateWidget(panel.querySelector(".pp-revs-cta"));
  }

  function boot() {
    fetchFirstParty().then(function (fp) {
      if (mount(fp)) { window.__ppStarsMounted = true; return; }
      var tries = 0;
      var t = setInterval(function () {
        if (mount(fp) || ++tries > 20) { clearInterval(t); window.__ppStarsMounted = true; }
      }, 250);
    });
  }

  // Selkirk-style description accordion (Diego 09-10: "instead of these huge blobs
  // of text… copy this Selkirk style, like how it has the dropdown"). Client-side
  // fold of the EXISTING description — the source descriptionHtml is untouched
  // (coyote-set-claim.js owns it). The lead paragraphs BEFORE "Specifications:"
  // stay visible — on Coyote that is the standing claim, which must keep rendering.
  // Unknown description shapes are left alone (no markers → no-op).
  function accordionize() {
    var desc = document.querySelector(".product__info-container .product__description");
    if (!desc || desc.getAttribute("data-pp-acc")) return;
    var kids = Array.prototype.slice.call(desc.children);
    var specIdx = -1, descIdx = -1;
    for (var i = 0; i < kids.length; i++) {
      var t = (kids[i].textContent || "").trim().toLowerCase();
      if (specIdx < 0 && /^specifications:?$/.test(t)) specIdx = i;
      else if (descIdx < 0 && /^description:?$/.test(t)) descIdx = i;
    }
    if (specIdx < 0 || descIdx < 0 || descIdx <= specIdx) return;
    desc.setAttribute("data-pp-acc", "1");
    var sections = [
      ["Specifications", kids.slice(specIdx + 1, descIdx)],
      ["Description", kids.slice(descIdx + 1)]
    ];
    // Hide the "Specifications:"/"Description:" label paragraphs — the accordion
    // headers replace them.
    kids[specIdx].style.display = "none";
    kids[descIdx].style.display = "none";
    var acc = document.createElement("div");
    acc.className = "pp-acc";
    for (var s = 0; s < sections.length; s++) {
      var head = document.createElement("button");
      head.type = "button";
      head.className = "pp-acc-h";
      head.setAttribute("aria-expanded", "false");
      head.innerHTML = "<span>" + sections[s][0] + '</span><span class="pp-acc-c" aria-hidden="true"></span>';
      var body = document.createElement("div");
      body.className = "pp-acc-b";
      for (var e = 0; e < sections[s][1].length; e++) body.appendChild(sections[s][1][e]);
      acc.appendChild(head);
      acc.appendChild(body);
      head.addEventListener("click", (function (h, b) {
        return function () {
          var open = b.classList.toggle("open");
          h.setAttribute("aria-expanded", open ? "true" : "false");
          h.classList.toggle("open", open);
        };
      })(head, body));
    }
    desc.appendChild(acc);
  }

  // Diego 09-10: below the CTA the order is GUARANTEE → description (with its
  // Specifications/Description accordion) → the "See how it's built"/"See the
  // collection" `.pp-learn` rows. Those rows belong to proton-preorder.js (a
  // LOCKED surface — we move OUR elements, never edit theirs) and mount async,
  // AFTER us, anchored to the same CTA wrapper — so a one-shot insert gets
  // leapfrogged. This settle pass re-asserts the order for ~8s, idempotently.
  function orderBelowCta() {
    var tries = 0;
    var t = setInterval(function () {
      tries++;
      var cont = document.querySelector(".product__info-container");
      if (!cont) { if (tries > 16) clearInterval(t); return; }
      var form = cont.querySelector("product-form");
      var row = form && form.closest(".product__info-container > *");
      var desc = cont.querySelector(".product__description");
      var learn = cont.querySelector(".pp-learn");
      if (learn && learn.parentElement && desc && learn.previousElementSibling !== desc) {
        learn.parentElement.insertBefore(desc, learn);
      }
      if (tries > 16) clearInterval(t);
    }, 500);
  }

  // Mobile lightbox is a swipe carousel (polish CSS lays the media out
  // horizontally); Dawn only scrolls the ACTIVE image into view VERTICALLY,
  // so center it on the horizontal axis when the modal opens.
  document.addEventListener("click", function (e) {
    if (!e.target.closest || !e.target.closest(".product__media-toggle, .product__modal-opener")) return;
    if (!window.matchMedia("(max-width: 749px)").matches) return;
    setTimeout(function () {
      var act = document.querySelector("product-modal[open] .product-media-modal__content .active");
      if (act) act.scrollIntoView({ inline: "center", block: "nearest" });
    }, 120);
  });

  function bootAll() {
    boot();
    try { accordionize(); } catch (e) { /* never let the fold break the stars */ }
    try { orderBelowCta(); } catch (e) {}
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", bootAll);
  else bootAll();
})();
